import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';

// ── inline styles ────────────────────────────────────────────
const s = {
    page:   { padding: '24px' },
    title:  { fontWeight: 800, color: '#1e293b', marginBottom: 4, fontSize: 22 },
    sub:    { color: '#64748b', fontSize: 14, marginBottom: 24 },

    // Global card
    globalCard: {
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        padding: '24px 28px',
        marginBottom: 28,
        borderLeft: '5px solid #6366f1'
    },
    globalTitle: { fontWeight: 700, color: '#4338ca', marginBottom: 16, fontSize: 15 },
    row: { display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' },
    field: { display: 'flex', flexDirection: 'column', minWidth: 180 },
    label: { fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 },
    input: {
        padding: '10px 14px',
        border: '2px solid #e2e8f0',
        borderRadius: 10,
        fontSize: 15,
        outline: 'none',
        width: 160,
        boxSizing: 'border-box'
    },
    saveBtnGlobal: {
        padding: '10px 22px',
        background: '#6366f1',
        color: '#fff',
        border: 'none',
        borderRadius: 10,
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 14
    },

    // Alert
    alertOk:  { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontWeight: 600, fontSize: 13 },
    alertErr: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontWeight: 600, fontSize: 13 },

    // User table card
    tableCard: {
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
        overflow: 'hidden'
    },
    tableHead: {
        display: 'grid',
        gridTemplateColumns: '1fr 140px 160px 160px 110px',
        background: '#f1f5f9',
        padding: '12px 20px',
        fontWeight: 700,
        fontSize: 12,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    tableRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 140px 160px 160px 110px',
        padding: '14px 20px',
        alignItems: 'center',
        borderBottom: '1px solid #f1f5f9'
    },
    username: { fontWeight: 700, color: '#1e293b', fontSize: 14 },
    statusBadge: (active) => ({
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: active ? '#dcfce7' : '#fee2e2',
        color: active ? '#16a34a' : '#dc2626'
    }),
    miniInput: {
        padding: '8px 10px',
        border: '2px solid #e2e8f0',
        borderRadius: 8,
        fontSize: 13,
        width: 120,
        outline: 'none',
        boxSizing: 'border-box'
    },
    saveBtn: {
        padding: '8px 16px',
        background: '#0ea5e9',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 12
    },
    resetBtn: {
        padding: '8px 12px',
        background: '#f1f5f9',
        color: '#64748b',
        border: 'none',
        borderRadius: 8,
        fontWeight: 700,
        cursor: 'pointer',
        fontSize: 12
    },
    hint: { fontSize: 11, color: '#94a3b8', marginTop: 2 }
};
// ─────────────────────────────────────────────────────────────

const CommissionSetting = () => {
    const [loading, setLoading]           = useState(true);
    const [globalDep, setGlobalDep]       = useState('1');
    const [globalWit, setGlobalWit]       = useState('0');
    const [globalSaving, setGlobalSaving] = useState(false);
    const [globalMsg, setGlobalMsg]       = useState(null);  // { type, text }

    const [users, setUsers]               = useState([]);
    const [rechargeRequests, setRechargeRequests] = useState([]);
    const [reviewingId, setReviewingId] = useState(null);
    // userRates: { [userId]: { dep: string, wit: string, saving: bool, msg: { type, text } | null } }
    const [userRates, setUserRates]       = useState({});

    const flashGlobal = (type, text) => {
        setGlobalMsg({ type, text });
        setTimeout(() => setGlobalMsg(null), 3000);
    };

    const setUserField = (id, field, val) => {
        setUserRates(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
    };

    const flashUser = (id, type, text) => {
        setUserField(id, 'msg', { type, text });
        setTimeout(() => setUserField(id, 'msg', null), 3000);
    };

    const fetchAll = useCallback(() => {
        setLoading(true);
        socketService.emit('get_all_users_commission');
        socketService.emit('admin_get_commission_recharge_requests', { status: 'pending' });
    }, []);

    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) socketService.connect();

        const onConnect = () => fetchAll();

        const onAllUsers = (data) => {
            setLoading(false);
            if (!data.success) return;
            setGlobalDep(String(data.globalDepositRate  ?? 1));
            setGlobalWit(String(data.globalWithdrawRate ?? 0));
            setUsers(data.users || []);
            const rates = {};
            (data.users || []).forEach(u => {
                rates[u._id] = {
                    dep:    u.depositCommissionRate  !== null ? String(u.depositCommissionRate)  : '',
                    wit:    u.withdrawCommissionRate !== null ? String(u.withdrawCommissionRate) : '',
                    saving: false,
                    msg:    null
                };
            });
            setUserRates(rates);
        };

        const onGlobalUpdate = (data) => {
            setGlobalSaving(false);
            if (data.success) {
                setGlobalDep(String(data.depositCommissionRate));
                setGlobalWit(String(data.withdrawCommissionRate));
                flashGlobal('ok', data.message || 'Global rates saved!');
            } else {
                flashGlobal('err', data.message || 'Failed to save');
            }
        };

        const onUserUpdate = (data) => {
            const id = data.userId;
            setUserField(id, 'saving', false);
            if (data.success) {
                const depVal = data.depositCommissionRate;
                const witVal = data.withdrawCommissionRate;
                setUserRates(prev => ({
                    ...prev,
                    [id]: {
                        ...prev[id],
                        dep: depVal !== null ? String(depVal) : '',
                        wit: witVal !== null ? String(witVal) : '',
                        saving: false
                    }
                }));
                flashUser(id, 'ok', 'Saved!');
            } else {
                flashUser(id, 'err', data.message || 'Failed');
            }
        };

        const onRechargeRequests = (data) => {
            if (data.success) setRechargeRequests(data.requests || []);
        };

        const onRechargeReviewed = (data) => {
            setReviewingId(null);
            if (data.success) fetchAll();
            else flashGlobal('err', data.message || 'Recharge review failed');
        };

        const onRechargeChanged = () => {
            socketService.emit('admin_get_commission_recharge_requests', { status: 'pending' });
        };

        socketService.on('connect',                     onConnect);
        socketService.on('all_users_commission_response', onAllUsers);
        socketService.on('update_commission_rates_response', onGlobalUpdate);
        socketService.on('update_user_commission_response',  onUserUpdate);
        socketService.on('admin_commission_recharge_requests_response', onRechargeRequests);
        socketService.on('admin_review_commission_recharge_response', onRechargeReviewed);
        socketService.on('commission_recharge_requests_changed', onRechargeChanged);

        if (socket.connected) fetchAll();

        return () => {
            socketService.off('connect',                     onConnect);
            socketService.off('all_users_commission_response', onAllUsers);
            socketService.off('update_commission_rates_response', onGlobalUpdate);
            socketService.off('update_user_commission_response',  onUserUpdate);
            socketService.off('admin_commission_recharge_requests_response', onRechargeRequests);
            socketService.off('admin_review_commission_recharge_response', onRechargeReviewed);
            socketService.off('commission_recharge_requests_changed', onRechargeChanged);
        };
    }, [fetchAll]);

    const saveGlobal = (e) => {
        e.preventDefault();
        const dep = parseFloat(globalDep);
        const wit = parseFloat(globalWit);
        if (isNaN(dep) || dep < 0 || dep > 100) return flashGlobal('err', 'Deposit rate must be 0–100');
        if (isNaN(wit) || wit < 0 || wit > 100) return flashGlobal('err', 'Withdraw rate must be 0–100');
        setGlobalSaving(true);
        socketService.emit('update_commission_rates', { depositCommissionRate: dep, withdrawCommissionRate: wit });
    };

    const saveUser = (userId) => {
        const r = userRates[userId];
        if (!r) return;
        // empty string = reset to global (null)
        const depVal = r.dep.trim() === '' ? null : parseFloat(r.dep);
        const witVal = r.wit.trim() === '' ? null : parseFloat(r.wit);
        if (depVal !== null && (isNaN(depVal) || depVal < 0 || depVal > 100)) return flashUser(userId, 'err', 'Deposit rate 0–100');
        if (witVal !== null && (isNaN(witVal) || witVal < 0 || witVal > 100)) return flashUser(userId, 'err', 'Withdraw rate 0–100');
        setUserField(userId, 'saving', true);
        socketService.emit('update_user_commission', {
            userId,
            depositCommissionRate:  depVal,
            withdrawCommissionRate: witVal
        });
    };

    const resetUser = (userId) => {
        setUserRates(prev => ({ ...prev, [userId]: { ...prev[userId], dep: '', wit: '' } }));
    };

    const reviewRecharge = (requestId, action) => {
        setReviewingId(requestId);
        socketService.emit('admin_review_commission_recharge', { requestId, action });
    };

    if (loading) return <Layout><div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>⏳ Loading...</div></Layout>;

    return (
        <Layout>
            <div style={s.page}>
                <h2 style={s.title}>💰 Commission Rate Settings</h2>
                <p style={s.sub}>
                    Global default হলো সব user এর জন্য। কোনো user এর নিজস্ব rate set করলে সেটাই apply হবে।
                    খালি রাখলে global rate apply হবে।
                </p>

                {/* ── Global Rates ───────────────────────────── */}
                <div style={s.globalCard}>
                    <div style={s.globalTitle}>🌐 Global Default Rate (সব user এর জন্য)</div>

                    {globalMsg && (
                        <div style={globalMsg.type === 'ok' ? s.alertOk : s.alertErr}>
                            {globalMsg.type === 'ok' ? '✅' : '❌'} {globalMsg.text}
                        </div>
                    )}

                    <form onSubmit={saveGlobal}>
                        <div style={s.row}>
                            <div style={s.field}>
                                <label style={s.label}>📥 Deposit Rate (%)</label>
                                <input
                                    type="number" step="0.01" min="0" max="100"
                                    value={globalDep}
                                    onChange={e => setGlobalDep(e.target.value)}
                                    style={s.input}
                                    placeholder="e.g. 1.5"
                                />
                            </div>
                            <div style={s.field}>
                                <label style={s.label}>📤 Withdraw Rate (%)</label>
                                <input
                                    type="number" step="0.01" min="0" max="100"
                                    value={globalWit}
                                    onChange={e => setGlobalWit(e.target.value)}
                                    style={s.input}
                                    placeholder="e.g. 0.5"
                                />
                            </div>
                            <button type="submit" style={s.saveBtnGlobal} disabled={globalSaving}>
                                {globalSaving ? '⏳ Saving...' : '💾 Save Global'}
                            </button>
                        </div>
                    </form>
                </div>

                <div style={{ ...s.tableCard, marginBottom: 28 }}>
                    <div style={{ padding: '16px 20px', background: '#fff7ed', fontWeight: 800, color: '#c2410c' }}>
                        🔋 Pending App Balance Recharge Requests ({rechargeRequests.length})
                    </div>
                    {rechargeRequests.length === 0 ? (
                        <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No pending recharge request</div>
                    ) : rechargeRequests.map(request => (
                        <div key={request._id} style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 180px 180px',
                            alignItems: 'center',
                            gap: 12,
                            padding: '14px 20px',
                            borderBottom: '1px solid #f1f5f9'
                        }}>
                            <div>
                                <div style={s.username}>{request.username}</div>
                                <div style={s.hint}>{new Date(request.createdAt).toLocaleString()}</div>
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 800, color: '#0f766e' }}>
                                ৳{Number(request.amount || 0).toFixed(2)}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    style={{ ...s.saveBtn, background: '#16a34a' }}
                                    disabled={reviewingId === request._id}
                                    onClick={() => reviewRecharge(request._id, 'approve')}
                                >Approve</button>
                                <button
                                    style={{ ...s.resetBtn, color: '#dc2626' }}
                                    disabled={reviewingId === request._id}
                                    onClick={() => reviewRecharge(request._id, 'reject')}
                                >Reject</button>
                            </div>
                        </div>
                    ))}
                </div>

                {/* ── Per-user Table ─────────────────────────── */}
                <div style={s.tableCard}>
                    <div style={s.tableHead}>
                        <span>User</span>
                        <span>App Balance</span>
                        <span>Deposit Rate (%)</span>
                        <span>Withdraw Rate (%)</span>
                        <span>Action</span>
                    </div>

                    {users.length === 0 && (
                        <div style={{ padding: '32px', textAlign: 'center', color: '#94a3b8' }}>No users found</div>
                    )}

                    {users.map(u => {
                        const r = userRates[u._id] || { dep: '', wit: '', saving: false, msg: null };
                        const effectiveDep = r.dep.trim() !== '' ? r.dep : `${globalDep} (global)`;
                        const effectiveWit = r.wit.trim() !== '' ? r.wit : `${globalWit} (global)`;

                        return (
                            <div key={u._id} style={s.tableRow}>
                                {/* Username + status */}
                                <div>
                                    <div style={s.username}>{u.username}</div>
                                    <span style={s.statusBadge(u.status === 'active')}>
                                        {u.status === 'active' ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <div>
                                    <div style={{ fontWeight: 800, color: u.canTransact ? '#16a34a' : '#dc2626' }}>
                                        ৳{Number(u.availableBalance || 0).toFixed(2)}
                                    </div>
                                    <div style={s.hint}>Total ৳{Number(u.balance || 0).toFixed(2)}</div>
                                    {Number(u.reservedBalance || 0) > 0 && (
                                        <div style={s.hint}>Reserved ৳{Number(u.reservedBalance).toFixed(2)}</div>
                                    )}
                                </div>

                                {/* Deposit rate input */}
                                <div>
                                    <input
                                        type="number" step="0.01" min="0" max="100"
                                        value={r.dep}
                                        onChange={e => setUserField(u._id, 'dep', e.target.value)}
                                        style={s.miniInput}
                                        placeholder={`${globalDep} (global)`}
                                    />
                                    <div style={s.hint}>Apply: {effectiveDep}%</div>
                                </div>

                                {/* Withdraw rate input */}
                                <div>
                                    <input
                                        type="number" step="0.01" min="0" max="100"
                                        value={r.wit}
                                        onChange={e => setUserField(u._id, 'wit', e.target.value)}
                                        style={s.miniInput}
                                        placeholder={`${globalWit} (global)`}
                                    />
                                    <div style={s.hint}>Apply: {effectiveWit}%</div>
                                </div>

                                {/* Save / Reset + msg */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <button
                                            style={s.saveBtn}
                                            onClick={() => saveUser(u._id)}
                                            disabled={r.saving}
                                        >
                                            {r.saving ? '⏳' : '💾'}
                                        </button>
                                        <button
                                            style={s.resetBtn}
                                            title="Reset to global"
                                            onClick={() => resetUser(u._id)}
                                        >
                                            ↺
                                        </button>
                                    </div>
                                    {r.msg && (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: r.msg.type === 'ok' ? '#16a34a' : '#dc2626' }}>
                                            {r.msg.type === 'ok' ? '✅' : '❌'} {r.msg.text}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Info */}
                <div style={{ marginTop: 20, background: '#f8fafc', borderRadius: 10, padding: '14px 18px', borderLeft: '4px solid #6366f1' }}>
                    <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.7 }}>
                        <strong>↺ Reset</strong> বাটন চাপলে user এর নিজস্ব rate সরে যাবে এবং global rate apply হবে।
                        Rate change করলে নতুন commission calculation থেকে immediately apply হবে।
                    </p>
                </div>
            </div>
        </Layout>
    );
};

export default CommissionSetting;
